import { useEffect, useState } from "react";
import { Gift, Bell, FileSpreadsheet } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

function Kpi({ label, value, testId }) {
  return (
    <div className="rounded-lg bg-secondary/40 p-3" data-testid={testId}>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className="font-display font-bold text-xl">{value}</div>
    </div>
  );
}

export function RankDiscountsPanel() {
  const [d, setD] = useState(null);
  useEffect(() => { api.rankDiscountStats().then(setD).catch(() => {}); }, []);
  const remind = async () => {
    try {
      const r = await api.sendRankDiscountReminders();
      toast.success(`Promemoria sconti: ${r.sent} inviati${r.failed ? `, ${r.failed} falliti` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invio non riuscito");
    }
  };
  if (!d) return null;
  return (
    <Card className="p-5 bg-card border-border space-y-3" data-testid="rank-discounts-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><Gift className="h-4 w-4" /> Sconti classifica clienti</h3>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={remind} data-testid="rank-disc-remind">
            <Bell className="h-3.5 w-3.5" /> Promemoria ora
          </Button>
          <a href={api.rankDiscountExportUrl()} className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-xs border border-border hover:bg-secondary/60" data-testid="rank-disc-export">
            <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
          </a>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Kpi label="Emessi" value={d.issued} testId="rank-disc-issued" />
        <Kpi label="Riscattati" value={d.redeemed} testId="rank-disc-redeemed" />
        <Kpi label="Tasso riscatto" value={`${d.rate}%`} testId="rank-disc-rate" />
      </div>
      {d.issued === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="rank-disc-empty">Nessun codice sconto emesso: attiva il premio nella tab WhatsApp → Automazione globale.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1.5">Riscatti per sede</div>
            {d.by_store.length === 0 ? <p className="text-muted-foreground text-xs">Ancora nessun riscatto</p> : (
              <ul className="space-y-1" data-testid="rank-disc-by-store">
                {d.by_store.map((s) => <li key={s.store} className="flex justify-between"><span>{s.store}</span><b>{s.redeemed}</b></li>)}
              </ul>
            )}
          </div>
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1.5">Per mese (emessi · riscattati · scaduti)</div>
            <ul className="space-y-1" data-testid="rank-disc-by-month">
              {d.by_month.map((m) => <li key={m.month} className="flex justify-between"><span className="font-mono-code">{m.month}</span><span>{m.issued} · <b className="text-emerald-400">{m.redeemed}</b> · <span className="text-muted-foreground">{m.expired}</span></span></li>)}
            </ul>
          </div>
          {d.recent.length > 0 && (
            <div className="md:col-span-2">
              <div className="text-xs font-semibold text-muted-foreground mb-1.5">Ultimi riscatti</div>
              <ul className="divide-y divide-border/60" data-testid="rank-disc-recent">
                {d.recent.map((r) => <li key={r.code} className="py-1.5 flex gap-3 text-xs"><span className="font-mono-code text-muted-foreground w-24">{fmt(r.redeemed_at)}</span><span className="font-mono-code">{r.code}</span><span className="text-muted-foreground">{r.store}</span></li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
